import React, { useEffect } from 'react';
import {
  CustomSlider,
  formatValuta,
  responsivePadding,
  ScenarioOption,
  CustomCurrencyInput,
  CashflowIcon,
} from '@myfaro/ui-react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Divider, Grid, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../../core/profile';
import { Goals as GoalsClass, ScenarioType } from '../../core/types';
import { getScenarios } from '../../helpers/scenarios';
import config from '../../core/context/config/config';
import { getAdditionalDomains } from '../../helpers/additionalDomains';

const useStyles = makeStyles(theme => ({
  body: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    paddingTop: theme.spacing(4),
    ...responsivePadding(theme),
  },
  lightText: {
    color: theme.palette.text.secondary,
  },
  scenarioBlock: {
    margin: `${theme.spacing(2)}px 0`,
  },
  additionalDomainsBlock: {
    marginBottom: theme.spacing(4),
  },
  gridItem: {
    padding: `${theme.spacing(1)}px 0`,
    [theme.breakpoints.up('md')]: {
      '&:nth-child(odd)': {
        paddingLeft: 0,
        paddingRight: theme.spacing(1),
      },
      '&:nth-child(even)': {
        paddingRight: 0,
        paddingLeft: theme.spacing(1),
      },
    },
  },
  valueInput: {
    marginTop: theme.spacing(2),
  },
}));

const scenarios: ScenarioType[] = getScenarios(config.scenarios);
const additionalDomainsArray: any = getAdditionalDomains(
  config.additionalDomains,
);

function Goals() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { useProfile, onChange } = useProfileContext();
  const data = useProfile<GoalsClass>('goals');
  const onChangeGoals = onChange('goals');

  const { retirement, additionalDomains, notes } = data;

  const monthlySavingsGoal = {
    name: 'monthlySavingsValue',
    title: t('goals.monthlySavingGoal.monthlySavings.title'),
    description: t('goals.monthlySavingGoal.monthlySavings.description'),
    type: 'slider',
    rangeValues: { min: 0, max: 5000, step: 100 },
    basicValue: 500,
  };

  const desiredRetirementBudgetValueGoal = {
    name: 'desiredRetirementBudgetValue',
    title: t('goals.scenarios.desiredRetirementBudgetValue.title'),
    description: t('goals.scenarios.desiredRetirementBudgetValue.description'),
    rightIcon: <CashflowIcon />,
    type: 'slider',
    rangeValues: { min: 0, max: 5000, step: 100 },
    basicValue: 2500,
  };

  useEffect(() => {
    // initialise the value of monthlySavings on init
    if (!data.monthlySavingsValue) {
      onChangeGoals({
        monthlySavingsValue: monthlySavingsGoal.basicValue,
      });
    }
  }, []);

  return (
    <Box className={classes.body}>
      <Box mb={2}>
        <Typography color='primary' variant='h5'>
          {t('goals.title')}
        </Typography>
      </Box>
      <Box mb={2}>
        <Typography variant='body2' className={classes.lightText}>
          {t('goals.subTitle')}
        </Typography>
      </Box>
      <Grid className={classes.scenarioBlock} container>
        <Grid className={classes.gridItem} item md={6}>
          <ScenarioOption selected {...monthlySavingsGoal}>
            <CustomSlider
              onChangeValue={(value?: number | number[]) => {
                if (typeof value === 'number') {
                  onChangeGoals({
                    monthlySavingsValue: value,
                  });
                }
              }}
              color='primary'
              value={data.monthlySavingsValue ?? monthlySavingsGoal.basicValue}
              min={monthlySavingsGoal.rangeValues.min}
              max={monthlySavingsGoal.rangeValues.max}
              step={monthlySavingsGoal.rangeValues.step}
              formatter={formatValuta}
            />
          </ScenarioOption>
        </Grid>
        <Grid className={classes.gridItem} item md={6}>
          <ScenarioOption selected {...desiredRetirementBudgetValueGoal}>
            <CustomSlider
              onChangeValue={(value?: number | number[]) => {
                if (typeof value === 'number') {
                  onChangeGoals({
                    retirement: {
                      ...retirement,
                      desiredRetirementBudgetValue: value,
                    },
                  });
                }
              }}
              color='primary'
              value={
                data.retirement?.desiredRetirementBudgetValue ??
                desiredRetirementBudgetValueGoal.basicValue
              }
              min={desiredRetirementBudgetValueGoal.rangeValues.min}
              max={desiredRetirementBudgetValueGoal.rangeValues.max}
              step={desiredRetirementBudgetValueGoal.rangeValues.step}
              formatter={formatValuta}
            />
          </ScenarioOption>
        </Grid>
      </Grid>
      <Box my={2}>
        <Divider variant='middle' />
      </Box>
      <Box my={2}>
        <Typography variant='h6'>{t('goals.scenarios.title')}</Typography>
      </Box>
      <Box my={2}>
        <Typography variant='body2' className={classes.lightText}>
          {t('goals.scenarios.subTitle')}
        </Typography>
      </Box>
      <Grid className={classes.scenarioBlock} container>
        {scenarios.map(
          ({
            name: scenarioName,
            type,
            rangeValues,
            basicValue,
            ...props
          }: ScenarioType) => (
            <Grid key={scenarioName} className={classes.gridItem} item md={6}>
              <ScenarioOption
                title={t(`goals.scenarios.${scenarioName}.title`)}
                description={t(`goals.scenarios.${scenarioName}.description`)}
                selected={retirement?.[scenarioName] !== undefined}
                onClick={() => {
                  onChangeGoals({
                    retirement: {
                      ...retirement,
                      [scenarioName]:
                        retirement?.[scenarioName] !== undefined
                          ? undefined
                          : basicValue,
                    },
                  });
                }}
                {...props}>
                <>
                  {type === 'slider' && (
                    <CustomSlider
                      onChangeValue={(value?: number | number[]) => {
                        if (typeof value === 'number') {
                          onChangeGoals({
                            retirement: {
                              ...retirement,
                              [scenarioName]: value,
                            },
                          });
                        }
                      }}
                      color='primary'
                      value={retirement?.[scenarioName]}
                      min={rangeValues.min}
                      max={rangeValues.max}
                      step={rangeValues.step}
                    />
                  )}
                  {type === 'valueInput' && (
                    <div className={classes.valueInput}>
                      <CustomCurrencyInput
                        value={retirement?.[scenarioName]}
                        onChange={(value: number) => {
                          // 'undefined' would trigger unselecting the option,
                          // so let's give it value null instead
                          const newValue = value === undefined ? null : value;

                          onChangeGoals({
                            retirement: {
                              ...retirement,
                              [scenarioName]: newValue,
                            },
                          });
                        }}
                      />
                    </div>
                  )}
                </>
              </ScenarioOption>
            </Grid>
          ),
        )}
      </Grid>
      <Box my={2}>
        <Divider variant='middle' />
      </Box>
      <Box mb={4}>
        <Typography variant='h6'>{t('goals.otherOptions.title')}</Typography>
      </Box>
      <Grid container spacing={2}>
        {additionalDomainsArray.map(({ name: domainName }: any) => (
          <Grid key={domainName} className={classes.gridItem} item md={6}>
            <ScenarioOption
              selected={!!additionalDomains?.includes(domainName)}
              onClick={() => {
                const clonedAdditionalDomains = Object.assign(
                  [],
                  additionalDomains,
                );
                const additionalDomainsUpdated = clonedAdditionalDomains?.includes(
                  domainName,
                )
                  ? clonedAdditionalDomains?.filter(
                      value => value !== domainName,
                    )
                  : [...clonedAdditionalDomains, domainName];

                onChangeGoals({
                  additionalDomains: [...additionalDomainsUpdated],
                });
              }}
              title={t(`goals.otherOptions.${domainName}.title`)}
              description={t(`goals.otherOptions.${domainName}.description`)}
            />
          </Grid>
        ))}
        <Grid item xs={12} sm={12}>
          <Box mb={2}>
            <TextField
              multiline
              placeholder={t('label.notes')}
              variant={'outlined'}
              fullWidth
              value={notes}
              onChange={event => {
                onChangeGoals({ notes: event.target.value });
              }}
              rows='3'
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Goals;
